import {
  IS_APP,
  IS_DEV_QUERY,
  IS_NODE,
  IS_NODE_BROWSERLESS,
} from "@/__main__/constants.mjs";
import { readData } from "@/__main__/get-data.mjs";
import blitzMessage, { EVENTS } from "@/__main__/ipc-core.mjs";
import router, { updateRoute } from "@/__main__/router.mjs";
import { setVolatileKV } from "@/app/actions.mjs";
import gamesList from "@/app/games.mjs";
import { GAME_SYMBOL_APEX } from "@/game-apex/definition-symbol.mjs";
import { GAME_SYMBOL_CS2 } from "@/game-csgo/definition-symbol.mjs";
import { GAME_SYMBOL_LOL } from "@/game-lol/definition-symbol.mjs";
import { GAME_SYMBOL_TFT } from "@/game-tft/definition-symbol.mjs";
import { devError } from "@/util/dev.mjs";
import lazyFn from "@/util/lazy-fn.mjs";
import retry from "@/util/retry-promise.mjs";
import SymbolMap from "@/util/symbol-map.mjs";

const lolFetchStaticData = lazyFn(
  () => import("@/game-lol/fetches/lol-fetch-static-data.mjs"),
);
// CSGO
const csgoFetchMatchData = lazyFn(
  () => import("@/game-csgo/csgo-fetch-match-data.mjs"),
);
// TFT
const tftFetchInGame = lazyFn(
  () => import("@/game-tft/tft-fetch-in-game-data.mjs"),
);
const fetchStreamerOverlayData = lazyFn(
  () => import("@/game-tft/tft-fetch-streamer-overlay-data.mjs"),
);
// Apex
const apexFetchMatchData = lazyFn(
  () => import("@/game-apex/apex-fetch-match-data.mjs"),
);
const apexFetchOverlaysData = lazyFn(
  () => import("@/game-apex/apex-fetch-overlays-data.mjs"),
);
const apexFetchProfileData = lazyFn(
  () => import("@/game-apex/apex-redirect-overlay-legend-select.mjs"),
);
const apexRedirectBenchmarking = lazyFn(
  () => import("@/game-apex/apex-redirect-overlay-benchmarking.mjs"),
);
const apexRedirectLegendSelect = lazyFn(
  () => import("@/game-apex/apex-redirect-overlay-legend-select.mjs"),
);

const CRITICAL_STATE_PATHS = [["settings"], ["user"]];

async function initializeUserData() {
  try {
    await Promise.all(
      CRITICAL_STATE_PATHS.map((path) => {
        return readData(path, { forceReadFromDB: true });
      }),
    );
  } catch (e) {
    devError("FAILED TO INITIALIZE USER DATA", e);
  }
}

const overlayRoutesPerGame = new SymbolMap({
  ...gamesList.reduce((hash, { gameSymbol, overlayRoutes }) => {
    if (Array.isArray(overlayRoutes)) {
      hash[gameSymbol] = overlayRoutes;
    }
    return hash;
  }, {}),

  // TODO: since the introduction of overlayRoutes into game definition,
  // these should be moved into game definitions.
  [GAME_SYMBOL_LOL]: [
    {
      path: "/lol/overlay/loading-screen",
      component: "game-lol/LoadingScreen.jsx",
      async fetchData() {
        // Get game data as a side effect.
        if (IS_APP) {
          retry(() => blitzMessage(EVENTS.DB_READ, "LOL_LOADING_SCREEN_DATA"), {
            maxRetries: 5,
          }).then((v) => {
            let json = {};
            try {
              json = JSON.parse(v);
            } catch (e) {
              devError("Failed to parse LOL_LOADING_SCREEN_DATA", e);
            }
            setVolatileKV("LoLLoadingState", json);
          });
          retry(
            () =>
              new Promise((resolve, reject) => {
                blitzMessage(EVENTS.DB_READ, "LOL_LIVECLIENT_DATA")
                  .then((v) => {
                    const json = JSON.parse(v);
                    if (!json?.allPlayers || json.allPlayers.length === 0)
                      throw new Error("Invalid Live Client data!");
                    // lolFetchLoadingScreenData(
                    //   updateRosterRequests(json.allPlayers),
                    // );
                    resolve(json);
                  })
                  .catch((err) => {
                    reject(err);
                  });
              }),
            { maxRetries: 5, interval: 200 },
          )
            .then((v) => {
              setVolatileKV("LoLLiveClient", v);
            })
            .catch(() => {});
        }
        if ((!IS_APP && !IS_NODE_BROWSERLESS) || IS_DEV_QUERY) {
          const { default: PLAYER_DATA_FIXTURE } = await import(
            "@/game-lol/fixtures/lol-loadingstate-fixture.json"
          );
          await setVolatileKV("LoLLoadingState", PLAYER_DATA_FIXTURE);
          const { default: LIVE_CLIENT_FIXTURE } = await import(
            "@/game-lol/fixtures/lol-liveclient-fixture.json"
          );
          await setVolatileKV("LoLLiveClient", LIVE_CLIENT_FIXTURE);
        }

        return Promise.all([lolFetchStaticData()]).then(() => {
          return Promise.resolve();
        });
      },
    },
  ],
  [GAME_SYMBOL_TFT]: [
    {
      path: "/tft/overlay/in-game",
      component: "game-tft/InGame.jsx",
      fetchData: tftFetchInGame,
    },
    {
      path: "/tft/overlay/stream",
      component: "game-tft/InGameOverlayStreamInfoExternal.jsx",
      async fetchData(params, searchParams) {
        await fetchStreamerOverlayData(params, searchParams);

        // This will trigger an intentional infinite loop.
        setTimeout(() => {
          const {
            route: { currentPath, searchParams, state },
          } = router;
          updateRoute(currentPath, searchParams, state);
        }, 45 * 1000);
      },
    },
  ],
  [GAME_SYMBOL_APEX]: [
    {
      path: "/apex/overlay",
      component: "game-apex/Overlay.jsx",
    },
    {
      path: "/apex/overlay/benchmark",
      component: "game-apex/BenchmarkOverlay.jsx",
      fetchData: async function () {
        await initializeUserData();
        apexFetchMatchData(...arguments);
      },
      redirect: apexRedirectBenchmarking,
    },
    {
      path: "/apex/overlay/legend-select",
      component: "game-apex/LegendSelectOverlay.tsx",
      fetchData: async function () {
        await initializeUserData();
        const [_, searchParams] = arguments;
        searchParams.append("apexPlatformId", searchParams.get("profileId"));
        apexFetchOverlaysData(...arguments);
        apexFetchProfileData(...arguments);
      },
      redirect: apexRedirectLegendSelect,
    },
    {
      path: "/apex/overlay/stream",
      component: "game-apex/StreamerOverlay.jsx",
      async fetchData(...args) {
        try {
          await apexFetchOverlaysData(...args);
        } catch (e) {
          devError(e);
        }

        // This will trigger an intentional infinite loop.
        setTimeout(() => {
          const {
            route: { currentPath, searchParams, state },
          } = router;
          updateRoute(currentPath, searchParams, state);
        }, 45 * 1000);
      },
    },
  ],
  [GAME_SYMBOL_CS2]: [
    {
      path: "/cs2/overlay",
      component: "game-csgo/Overlay.jsx",
    },
    {
      path: "/cs2/overlay/benchmark",
      component: "game-csgo/BenchmarkOverlay.tsx",
      fetchData: async function () {
        await initializeUserData();
        return csgoFetchMatchData(...arguments);
      },
      redirect: async (_, searchParams) => {
        if (!IS_APP && !IS_NODE && !searchParams.toString()) {
          // Fixtures lazy
          const { default: CsgoFixtures } = await import(
            "@/game-csgo/csgo-fixtures.json"
          );

          updateRoute(
            "/cs2/overlay/benchmark",
            `profileId=${CsgoFixtures.profileId}&matchId=${CsgoFixtures.matchId}`,
            { isRedirect: true, isUpdate: false },
          );

          return;
        }

        return false;
      },
    },
  ],
});

export default overlayRoutesPerGame;
